import { Badge, LoadingSpinner, Typography } from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import type {
  MatterLitigationDatePlanDate,
  MatterLitigationDateType,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import pluralize from "pluralize";

const formatDeadline = (
  value: number | string | undefined,
): string | undefined => {
  if (value === undefined || value === "") {
    return undefined;
  }

  const parsedValue =
    typeof value === "number" ? value : Number.parseFloat(value);

  if (!Number.isFinite(parsedValue)) {
    return String(value);
  }

  const days = Math.abs(parsedValue);

  if (days === 0) {
    return "0 days";
  }

  if (parsedValue < 0) {
    return `${pluralize("day", days, true)} before`;
  }

  return `${pluralize("day", days, true)} after`;
};

interface LitigationPlanTriggersAccordionProps {
  planDates: MatterLitigationDatePlanDate[];
  litigationDateTypes: MatterLitigationDateType[];
  isLoading?: boolean;
  className?: string;
}

export const LitigationPlanTriggersAccordion = ({
  planDates,
  litigationDateTypes,
  isLoading,
  className,
}: LitigationPlanTriggersAccordionProps) => {
  if (isLoading) {
    return (
      <Accordion
        type="single"
        collapsible
        className={cn("w-full text-xs", className)}
      >
        <AccordionItem
          value="plan-triggers"
          className="bg-card border-border rounded-md border shadow-sm"
        >
          <AccordionTrigger className="px-3 py-2 hover:no-underline">
            <div className="flex flex-col text-left">
              <Typography size="sm" weight="medium" className="leading-tight">
                Plan triggers
              </Typography>
              <Typography size="xs" className="text-muted-foreground">
                Loading plan triggers...
              </Typography>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <LoadingSpinner />
              <span>Fetching plan trigger relationships...</span>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  if (!planDates.length) {
    return null;
  }

  const typeLabelById = new Map<string, string>(
    litigationDateTypes.map((type) => [type.id, type.label]),
  );

  const triggersMap = new Map<
    string,
    { triggerLabel: string; dates: MatterLitigationDatePlanDate[] }
  >();

  planDates.forEach((planDate) => {
    const triggerId = String(planDate.triggerId);
    const dateTypeId = String(planDate.typeId);
    if (!triggerId || !dateTypeId) {
      return;
    }

    const triggerLabel =
      typeLabelById.get(triggerId) ?? `Unknown trigger (${triggerId})`;

    const existing = triggersMap.get(triggerId);
    if (existing) {
      existing.dates.push(planDate);
    } else {
      triggersMap.set(triggerId, {
        triggerLabel,
        dates: [planDate],
      });
    }
  });

  const triggers = Array.from(triggersMap.entries());

  if (!triggers.length) {
    return null;
  }

  return (
    <Accordion
      type="single"
      collapsible
      className={cn("w-full text-xs", className)}
    >
      <AccordionItem
        value="plan-triggers"
        className="bg-card border-border rounded-md border shadow-sm"
      >
        <AccordionTrigger className="px-3 py-2 hover:no-underline">
          <div className="flex flex-col text-left">
            <Typography size="sm" weight="medium" className="leading-tight">
              Plan triggers
            </Typography>
            <Typography size="xs" className="text-muted-foreground">
              {pluralize("trigger", triggers.length, true)}
            </Typography>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          <ul className="space-y-1.5">
            {triggers.map(([triggerId, { triggerLabel, dates }]) => (
              <li key={triggerId} className="text-foreground text-xs">
                <div className="space-y-1">
                  {dates.map((date) => {
                    const baseLabel =
                      typeLabelById.get(String(date.typeId)) ??
                      String(date.typeId);
                    const deadlineLabel = formatDeadline(Number(date.deadline));

                    return (
                      <div
                        key={date.id}
                        className="flex max-w-full items-center gap-1"
                      >
                        <Badge
                          variant="outline"
                          className="max-w-full min-w-0 font-medium"
                        >
                          <span className="truncate">{baseLabel}</span>
                        </Badge>
                        <span className="text-muted-foreground shrink-0">
                          is triggered{" "}
                          {deadlineLabel ? `${deadlineLabel} ` : "relative to "}
                        </span>
                        <Badge
                          variant="outline"
                          className="max-w-full min-w-0 font-medium"
                        >
                          <span className="truncate">{triggerLabel}</span>
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
